/**
 * Live progress for one acquisition run.
 *
 * Polls every 2 seconds while the run is live and stops the moment it reaches a
 * terminal state - a finished run is not going to change, and an interval left
 * running against a completed job is how a "cheap" poll becomes 43,000 requests
 * a day.
 *
 * The progress figure is deliberately hedged. It counts against `hits` as
 * reported when the run started, and the register moves underneath a long run,
 * so the bar can reach 100% with a few companies still to go, or finish at 98%.
 * That is honest; asserting an exact fraction would not be.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { acquisitionApi } from '../lib/api.js';
import {
  IconAlert,
  IconCheck,
  IconChevronRight,
  IconPause,
  IconPlay,
  IconSpinner,
  IconStop,
  IconTrash,
} from './Icons.jsx';

const POLL_INTERVAL_MS = 2000;
const LIVE_STATUSES = ['queued', 'running'];

// Exported so the log page's compact list/detail rows can render the same
// status badge and number formatting as the full card - a run must not look
// "Completed" in one view and something else in another.
export const STATUS_META = {
  queued: { label: 'Queued', tone: 'badge-info' },
  running: { label: 'Running', tone: 'badge-info' },
  paused: { label: 'Paused', tone: 'badge-warn' },
  completed: { label: 'Completed', tone: 'badge-ok' },
  failed: { label: 'Incomplete', tone: 'badge-error' },
  cancelled: { label: 'Cancelled', tone: 'badge-warn' },
};

// What each value of the API's `phase` field means, said in the same plain
// language the progress bar's own caption uses ("found"/"profiled").
// Exported so the log's compact rows and List table caption their status
// badge the same way the full card does - a run must not read "still going,
// somewhere" in one view and name its actual phase in another.
export const PHASE_LABEL = {
  list: 'finding companies',
  profile: 'profiling',
  financials: 'reading financials',
  directors: 'resolving directors',
  websites: 'finding websites',
  emails: 'guessing emails',
};

/**
 * The one thing "Running" alone can't say: which of up to six phases a
 * deeper-than-`list` run is actually on right now. A `full`-depth run can
 * spend hours in any one of them, and the status badge alone cannot tell an
 * operator "still going" from "stuck" - this is what makes that
 * distinguishable at a glance, no click into the run required.
 *
 * Null for a `list`-depth run (list is its only phase, so "Running" already
 * says everything this would) and for anything not currently paused or
 * running (a queued run has not started any phase yet; a finished one is not
 * "on" a phase any more).
 */
export function phaseCaption(run) {
  if (run.enrichmentDepth === 'list') return null;
  if (run.status !== 'running' && run.status !== 'paused') return null;
  return PHASE_LABEL[run.phase] ?? null;
}

export function formatNumber(value) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

// The bar carries the verdict too: a run that ended with a coverage gap is
// not green, however much of it succeeded. Exported so the compact List and
// List + detail rows can colour their own mini progress bar identically to
// the full card, for the same run.
export function progressBarTone(run) {
  return run.coverageGap || run.status === 'failed'
    ? 'is-error'
    : run.status === 'completed'
      ? 'is-ok'
      : run.status === 'paused' || run.status === 'cancelled'
        ? 'is-warn'
        : '';
}

/**
 * Whether a run still has something worth showing "done vs outstanding" for.
 * A clean completion is always ~100% - the bar would say nothing the
 * "Completed" badge doesn't already say. Anything still moving, or that
 * stopped without finishing, does have that to show - in every view, not
 * just the full card.
 */
export function hasVisibleProgress(run) {
  const isLive = LIVE_STATUSES.includes(run.status);
  return run.totalHits !== null && (isLive || Boolean(run.coverageGap) || run.status !== 'completed');
}

function formatElapsed(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function AcquisitionProgress({
  run: initialRun,
  onChanged,
  linkToDetail = false,
  compact = false,
  // Optional. When given, a trash-icon button appears in the footer that
  // hands this run's SIC code back to the caller - the log page uses it to
  // open the same delete-category dialog the filter bar's trash icon opens,
  // scoped to this run's code, without this component needing to know
  // anything about that dialog itself.
  onDeleteCategory,
}) {
  const [run, setRun] = useState(initialRun);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const isLive = LIVE_STATUSES.includes(run.status);

  const refresh = useCallback(async () => {
    try {
      const result = await acquisitionApi.get(run.uuid);
      setRun(result.run);
      onChanged?.(result.run);
    } catch {
      // A failed poll is not worth surfacing - the next one in two seconds
      // will either work or the page will show the API as offline anyway.
    }
  }, [run.uuid, onChanged]);

  useEffect(() => {
    if (!isLive) {
      if (timerRef.current) clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [isLive, refresh]);

  const act = async (action) => {
    setBusy(true);
    try {
      const result = await acquisitionApi[action](run.uuid);
      setRun(result.run);
      onChanged?.(result.run);
    } catch {
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const meta = STATUS_META[run.status] ?? { label: run.status, tone: '' };
  const percent = run.percent ?? 0;
  const barTone = progressBarTone(run);
  const showProgress = hasVisibleProgress(run);

  return (
    <article className={`run-card ${isLive ? 'run-card-live' : ''}`}>
      <header className="run-head">
        <div className="run-title">
          <span className="mono run-code">{run.sicCode}</span>
          <span className="run-desc">{run.sicDescription}</span>
        </div>
        <span className={`badge ${meta.tone}`}>
          {run.status === 'running' ? <IconSpinner /> : null}
          {meta.label}
          {phaseCaption(run) ? ` · ${phaseCaption(run)}` : ''}
        </span>
      </header>

      {/* What the run was asked for, in a sentence. Rendered by the API from
          the stored filters so the list and the detail view cannot disagree
          about what a run did. Skipped in a grid cell - a full sentence per
          card, times a screen full of cards, is exactly the "too much text"
          a gallery view should not have; it is one click away via Open run. */}
      {!compact && run.filterSummary ? <p className="run-filters">{run.filterSummary}</p> : null}

      {/* The bar is only meaningful once the run knows its total. Once the run
          has moved past list depth into its per-company profile pass, the
          count it tracks switches from "found" to "profiled" - see
          `progressCount` on the API response - so the bar does not read 100%
          for a run that is, in reality, still going. */}
      {showProgress ? (
        <>
          <div
            className="progress tip"
            data-tip={`${formatNumber(run.progressCount ?? run.companiesFound)} of about ${formatNumber(run.totalHits)} companies ${run.inProfilePhase ? 'profiled' : 'found'}. The total is what Companies House reported when the run started - it drifts as companies incorporate and dissolve, so this is close, not exact.`}
          >
            <div className={`progress-bar ${barTone}`} style={{ width: `${Math.min(100, percent)}%` }} />
          </div>
          <div className="progress-meta">
            <span>
              {formatNumber(run.progressCount ?? run.companiesFound)} of about {formatNumber(run.totalHits)}
              {run.inProfilePhase ? ' profiled' : ''}
            </span>
            <span className="mono">{percent}%</span>
          </div>
        </>
      ) : null}

      {/* One dense line of "value label" counters, replacing a boxed stat
          grid - 18 of these in the log otherwise cost a full padded row each
          for numbers that are secondary to the status and the note below. A
          grid card only carries the three counters that answer "did this
          work" - the rest (profiled, API calls, pace, windows) are a click
          away via Open run, rather than repeated at gallery scale. */}
      <div className="run-meta">
        <span className="run-stat">
          <span className="run-stat-value">{formatNumber(run.companiesFound)}</span> found
        </span>
        <span className="run-stat">
          <span className="run-stat-value">{formatNumber(run.companiesNew)}</span> new
        </span>
        <span className="run-stat">
          <span className="run-stat-value">{formatNumber(run.companiesUpdated)}</span> updated
        </span>
        {!compact && run.enrichmentDepth !== 'list' ? (
          <span
            className="run-stat tip"
            data-tip="Taken to profile depth and classified: previous names, accounts and confirmation-statement dates, insolvency and charges flags."
          >
            <span className="run-stat-value">{formatNumber(run.companiesEnriched)}</span> profiled
          </span>
        ) : null}
        {!compact ? (
          <span
            className="run-stat tip"
            data-tip="Companies House calls this run has spent. Phase 3 measured full coverage at roughly one call per 2,000 companies."
          >
            <span className="run-stat-value">{formatNumber(run.apiCallsUsed)}</span> API calls
          </span>
        ) : null}
        {compact ? null : isLive ? (
          <>
            <span className="run-stat">
              <span className="run-stat-value">{formatNumber(run.ratePerMinute)}</span> per minute
            </span>
            <span className="run-stat">
              <span className="run-stat-value">{run.etaLabel ?? '—'}</span> remaining
            </span>
          </>
        ) : (
          <span className="run-stat">
            <span className="run-stat-value">{formatElapsed(run.durationSeconds ?? run.elapsedSeconds)}</span> took
          </span>
        )}
        {!compact && run.windows.completed + run.windows.pending > 0 ? (
          <span
            className="run-stat tip"
            data-tip="A SIC code above 10,000 companies is acquired as a series of incorporation-date windows, because that is the most a single Companies House query can return."
          >
            {run.windows.completed} window{run.windows.completed === 1 ? '' : 's'} done
            {run.windows.pending > 0 ? `, ${run.windows.pending} to go` : ''}
          </span>
        ) : null}
      </div>

      {/* A coverage gap is the one thing that must never be quiet: it means the
          run did not acquire everything, and it must not read as success. */}
      {run.coverageGap ? (
        <div className="alert alert-error">
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">Incomplete coverage</span>
            {run.coverageGap}
          </span>
        </div>
      ) : null}

      {run.lastError && !run.coverageGap ? (
        <div className="alert alert-warn">
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">
              {run.errorCount} error{run.errorCount === 1 ? '' : 's'}, retried and carried on
            </span>
            {run.lastError}
          </span>
        </div>
      ) : null}

      {/* A caption, not a banner - the header badge already says "Completed",
          so this only needs to add the one fact the badge can't: full
          coverage. Full .alert weight is reserved for gaps and errors above.
          Dropped entirely in a grid cell - repeating this sentence across a
          screen full of completed cards is noise the badge already covers. */}
      {!compact && run.status === 'completed' && !run.coverageGap ? (
        <p className="run-note run-note-ok">
          <IconCheck />
          Complete - every company Companies House holds under this code is in the registry.
        </p>
      ) : null}

      <footer className="run-actions">
        {run.status === 'running' || run.status === 'queued' ? (
          <>
            <button
              type="button"
              className="btn btn-sm tip"
              onClick={() => void act('pause')}
              disabled={busy}
              data-tip="Stops within one page and keeps the checkpoint. Resuming carries on from the same position."
            >
              <IconPause />
              Pause
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger tip"
              onClick={() => void act('cancel')}
              disabled={busy}
              data-tip="Stops within one page. Companies already acquired stay in the registry, and the run can be resumed later from the same checkpoint."
            >
              <IconStop />
              Cancel
            </button>
          </>
        ) : null}

        {run.status === 'paused' || run.status === 'failed' || run.status === 'cancelled' ? (
          <button
            type="button"
            className="btn btn-sm btn-primary tip"
            onClick={() => void act('resume')}
            disabled={busy}
            data-tip="Picks up from the last committed page - no duplicates, no gaps."
          >
            <IconPlay />
            Resume
          </button>
        ) : null}

        <span className="dialog-footer-spacer" />

        {onDeleteCategory ? (
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm tip tip-left"
            onClick={() => onDeleteCategory(run)}
            data-tip={`Permanently delete every company and every run for ${run.sicCode} from the registry and this log.`}
            aria-label={`Delete every company and run for ${run.sicCode}`}
          >
            <IconTrash />
          </button>
        ) : null}

        {/* The depth/started line is what overflowed a grid cell in Carded
            view - two action buttons plus this text plus Open run had nowhere
            to go at 340px. Dropped in compact mode; it is one click away. */}
        {!compact ? (
          <span className="text-sm text-dim">
            {run.enrichmentDepth} depth · started{' '}
            {run.startedAt ? new Date(run.startedAt).toLocaleString() : 'not yet'}
          </span>
        ) : null}

        {linkToDetail ? (
          <Link
            className="btn btn-sm tip tip-left"
            to={`/log/${run.uuid}`}
            data-tip="The filters this run used in full, its counters, and the per-company outcome trail with CSV export."
          >
            Open run
            <IconChevronRight />
          </Link>
        ) : null}
      </footer>
    </article>
  );
}
